import React, { useState, useRef, useEffect, useCallback } from "react";
import {
    Fab, Box, Paper, Typography, TextField, IconButton,
    Avatar, Chip, CircularProgress, Slide, Tooltip, Divider, List,
    ListItemButton, ListItemText, ListItemSecondaryAction
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import AddCommentIcon from "@mui/icons-material/AddComment";
import HistoryIcon from "@mui/icons-material/History";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlined";
import { chatMessage } from "../../services/SearchService";
import { useHistory } from "react-router-dom";

const PRIMARY = "#03407b";
const PRIMARY_DARK = "#02357a";
const PRIMARY_LIGHT = "#ebf4fb";

const SUGGESTIONS = [
    "Développeurs React seniors disponibles",
    "Candidats Python avec expérience fintech",
    "Profils DevOps à Paris ce mois-ci",
];

const GREETING = "Bonjour 👋 Je suis Gia, votre assistante RH Go&Dev.\n\nDécrivez le profil que vous recherchez ou posez-moi une question sur votre vivier de candidats.";


const getUserId = () => {
    try {
        const raw = localStorage.getItem("userData");
        if (!raw) return "anonymous";
        const d = JSON.parse(raw);
        return d?.uuid || d?.id || "anonymous";
    } catch { return "anonymous"; }
};

const storageKey = (userId) => `pia_history_${userId}`;

const FAB_SIZE = 58;
const FAB_MARGIN = 8;
const FAB_POS_KEY = "pia_fab_position";
const DEFAULT_FAB_POS = { right: 28, bottom: 28 };

const clampFabPos = ({ right, bottom }) => ({
    right: Math.min(Math.max(right, FAB_MARGIN), window.innerWidth - FAB_SIZE - FAB_MARGIN),
    bottom: Math.min(Math.max(bottom, FAB_MARGIN), window.innerHeight - FAB_SIZE - FAB_MARGIN),
});

const loadFabPos = () => {
    try {
        const raw = localStorage.getItem(FAB_POS_KEY);
        return raw ? clampFabPos(JSON.parse(raw)) : DEFAULT_FAB_POS;
    } catch { return DEFAULT_FAB_POS; }
};

const loadHistory = (userId) => {
    try {
        const raw = localStorage.getItem(storageKey(userId));
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
};

const saveHistory = (userId, data) => {
    try {
        localStorage.setItem(storageKey(userId), JSON.stringify(data));
    } catch {}
};

const makeSession = () => ({
    id: String(Date.now()),
    title: "Nouvelle conversation",
    createdAt: new Date().toISOString(),
    messages: [{ role: "assistant", content: GREETING, candidates: [] }],
});

const sessionTitle = (session) => {
    const firstUser = session.messages.find((m) => m.role === "user");
    if (!firstUser) return "Nouvelle conversation";
    const t = firstUser.content.trim();
    return t.length > 42 ? t.slice(0, 42) + "…" : t;
};

const formatDate = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 86400000);
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return "Hier";
    if (diff < 7) return `Il y a ${diff} jours`;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
};


const TypingDots = () => (
    <Box sx={{ display: "flex", gap: "5px", p: "6px 4px", alignItems: "center" }}>
        {[0, 1, 2].map((i) => (
            <Box key={i} sx={{
                width: 6, height: 6, borderRadius: "50%",
                backgroundColor: PRIMARY,
                animation: "piaBounce 1.2s infinite ease-in-out",
                animationDelay: `${i * 0.18}s`,
                "@keyframes piaBounce": {
                    "0%, 80%, 100%": { transform: "scale(0.5)", opacity: 0.35 },
                    "40%": { transform: "scale(1)", opacity: 1 },
                },
            }} />
        ))}
    </Box>
);


const inlineBold = (text) =>
    text.split(/\*\*(.*?)\*\*/g).map((part, i) =>
        i % 2 === 1
            ? <Box key={i} component="strong" sx={{ fontWeight: 700 }}>{part}</Box>
            : part
    );

const isTableRow = (line) => line.trim().startsWith("|") && line.trim().endsWith("|");
const isTableSep  = (line) => /^\|[\s|:_-]+\|$/.test(line.trim());

const parseTableCells = (line) =>
    line.trim().slice(1, -1).split("|").map((c) => c.trim());

const SKIP_COLS = new Set(["#", "n°", "no", "num", "numéro"]);

const renderTable = (lines, key) => {
    const rows = lines.filter((l) => !isTableSep(l)).map(parseTableCells);
    if (rows.length === 0) return null;
    const [headers, ...dataRows] = rows;
    const visibleIdx = headers
        .map((h, i) => ({ h, i }))
        .filter(({ h }) => !SKIP_COLS.has(h.toLowerCase().replace(/[^\w]/g, "")));

    return (
        <Box key={key} sx={{ my: 1 }}>
            {dataRows.map((cells, ri) => (
                <Box key={ri} sx={{
                    display: "flex", flexWrap: "wrap", gap: "4px 10px",
                    mb: 0.75, px: 1.25, py: 1,
                    borderRadius: 2, backgroundColor: "#f5f9fc",
                    border: "1px solid #d4e8f4",
                }}>
                    {visibleIdx.map(({ h, i }) => cells[i] !== undefined && cells[i] !== "" && (
                        <Box key={i} sx={{ display: "flex", gap: "3px", alignItems: "baseline" }}>
                            <Typography component="span" sx={{ fontSize: "0.7rem", color: "#777", fontWeight: 600, whiteSpace: "nowrap" }}>
                                {h}:
                            </Typography>
                            <Typography component="span" sx={{ fontSize: "1rem", color: "#1a1a2e" }}>
                                {inlineBold(cells[i])}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ))}
        </Box>
    );
};

const renderMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split("\n");
    const result = [];
    let i = 0;

    while (i < lines.length) {
        const raw = lines[i];

        if (isTableRow(raw)) {
            const tableLines = [];
            while (i < lines.length && (isTableRow(lines[i]) || isTableSep(lines[i]))) {
                tableLines.push(lines[i]);
                i++;
            }
            result.push(renderTable(tableLines, `t${i}`));
            continue;
        }

        if (/^-{3,}$/.test(raw.trim())) { i++; continue; }
        const h = raw.match(/^#{1,6}\s+(.*)/);
        if (h) { result.push(<Box key={i} component="span" sx={{ display: "block", fontWeight: 700, mt: result.length > 0 ? 1 : 0, mb: 0.25, fontSize: "1rem" }}>{inlineBold(h[1])}</Box>); i++; continue; }
        const b = raw.match(/^[-*]\s+(.*)/);
        if (b) { result.push(<Box key={i} component="span" sx={{ display: "flex", gap: "6px", mb: 0.25 }}><Box component="span" sx={{ flexShrink: 0 }}>•</Box><Box component="span">{inlineBold(b[1])}</Box></Box>); i++; continue; }
        if (raw.trim() === "") { result.push(<Box key={i} component="span" sx={{ display: "block", height: "6px" }} />); i++; continue; }
        result.push(<Box key={i} component="span" sx={{ display: "block", mb: 0.1 }}>{inlineBold(raw)}</Box>);
        i++;
    }
    return result.filter(Boolean);
};


const AVATAR_PALETTE = ["#2563eb","#7c3aed","#db2777","#059669","#d97706","#0891b2","#dc2626"];
const nameColor = (n = "") => {
    let h = 0;
    for (let i = 0; i < n.length; i++) h = n.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
};
const nameInitials = (n = "") =>
    n.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("");

const CandidateCard = ({ candidate, onClick }) => {
    const skills = [
        ...(candidate.technical_skills || []).slice(0, 3),
        ...(candidate.programming_languages || []).slice(0, 2),
    ].slice(0, 4);

    const rawScore = candidate.score;
    const normalized = rawScore != null && rawScore > 1 ? rawScore / 10 : rawScore;
    const score = normalized != null && normalized > 0.01 ? Math.round(normalized * 100) : null;
    const scoreColor = score >= 80 ? "#16a34a" : score >= 60 ? "#ea580c" : "#dc2626";

    const prenom = candidate.prenom || "";
    const nomFamille = candidate.nom || "";
    const name = candidate.full_name
        || (prenom || nomFamille ? `${prenom} ${nomFamille}`.trim() : "Candidat");

    const fonction = candidate.fonction || "";
    const location = candidate.location || candidate.localisation || "";
    const exp = candidate.years_of_experience;

    const meta = [location, exp != null && `${exp} ans exp.`].filter(Boolean).join(" · ");
    const bg = nameColor(name);

    return (
        <Box onClick={onClick} sx={{
            position: "relative", px: 1.5, py: 1.1, mb: 0.6,
            borderRadius: 2.5, backgroundColor: "#fff",
            border: "1px solid #e2e8f0", cursor: "pointer",
            overflow: "hidden",
            transition: "all 0.18s ease",
            "&::after": {
                content: '""', position: "absolute",
                left: 0, top: 0, bottom: 0, width: "3px",
                background: "linear-gradient(180deg, #03407b, #02357a)",
                opacity: 0,
                transition: "opacity 0.18s ease",
            },
            "&:hover": {
                borderColor: "#aed6f1",
                boxShadow: "0 4px 18px rgba(99,102,241,0.12)",
                transform: "translateX(3px)",
                "&::after": { opacity: 1 },
            },
        }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    background: `linear-gradient(135deg, ${bg}cc, ${bg})`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "0.58rem", fontWeight: 800, color: "#fff",
                    mt: "1px", boxShadow: `0 2px 6px ${bg}44`,
                }}>
                    {nameInitials(name)}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.15 }}>
                        <Typography fontWeight={700} color="#111827" noWrap
                            sx={{ fontSize: "0.84rem", flex: 1, mr: 0.75, letterSpacing: "0.01em" }}>
                            {name}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                            {score != null && (
                                <Box sx={{
                                    backgroundColor: scoreColor, color: "#fff",
                                    borderRadius: "5px", px: "6px", py: "1px",
                                    fontSize: "0.67rem", fontWeight: 800, letterSpacing: "0.02em",
                                    lineHeight: 1.7,
                                }}>
                                    {score}%
                                </Box>
                            )}
                            <OpenInNewIcon sx={{ fontSize: 12, color: "#9ca3af" }} />
                        </Box>
                    </Box>

                    {fonction && (
                        <Typography noWrap sx={{ fontSize: "1rem", color: PRIMARY, fontWeight: 600, mb: 0.2 }}>
                            {fonction}
                        </Typography>
                    )}

                    {meta && (
                        <Typography noWrap sx={{ fontSize: "0.69rem", color: "#6b7280", mb: skills.length ? 0.55 : 0 }}>
                            {meta}
                        </Typography>
                    )}

                    {skills.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "3px" }}>
                            {skills.map((s, i) => (
                                <Box key={i} sx={{
                                    px: "6px", py: "1px", borderRadius: "5px",
                                    backgroundColor: PRIMARY_LIGHT,
                                    color: PRIMARY,
                                    fontSize: "0.6rem", fontWeight: 600,
                                    border: "1px solid #aed6f1",
                                    lineHeight: 1.7,
                                }}>
                                    {s}
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};


const MessageBubble = ({ message, onCandidateClick }) => {
    const isUser = message.role === "user";
    return (
        <Box sx={{ display: "flex", flexDirection: isUser ? "row-reverse" : "row", alignItems: "flex-start", mb: 2, gap: 1 }}>
            <Avatar sx={{
                width: 30, height: 30, flexShrink: 0, mt: 0.25,
                background: isUser ? "linear-gradient(135deg, #94a3b8, #64748b)" : "linear-gradient(135deg, #0556a4, #03407b)",
                fontSize: "1rem",
                boxShadow: isUser ? "none" : "0 2px 8px rgba(99,102,241,0.3)",
            }}>
                {isUser ? <PersonIcon sx={{ fontSize: 16 }} /> : <AutoAwesomeIcon sx={{ fontSize: 15 }} />}
            </Avatar>
            <Box sx={{ maxWidth: "82%" }}>
                {message.content && (
                    <Paper elevation={0} sx={{
                        px: 1.75, py: 1.25,
                        borderRadius: 2.5,
                        borderTopLeftRadius: isUser ? 2.5 : 0,
                        borderTopRightRadius: isUser ? 0 : 2.5,
                        background: isUser ? "linear-gradient(135deg, #0556a4, #03407b)" : "#fff",
                        color: isUser ? "#fff" : "#1e293b",
                        border: isUser ? "none" : "1px solid #f1f5f9",
                        boxShadow: isUser ? "0 2px 10px rgba(99,102,241,0.25)" : "0 1px 4px rgba(0,0,0,0.06)",
                    }}>
                        <Typography variant="body2" component="div" sx={{ lineHeight: 1.65, fontSize: "1rem" }}>
                            {isUser ? message.content : renderMarkdown(message.content)}
                        </Typography>
                    </Paper>
                )}
                {message.candidates?.length > 0 && (
                    <Box sx={{ mt: message.content ? 1 : 0 }}>
                        {message.candidates.map((c, i) => <CandidateCard key={i} candidate={c} onClick={() => onCandidateClick(c)} />)}
                    </Box>
                )}
            </Box>
        </Box>
    );
};


const HistoryPanel = ({ sessions, currentId, onSelect, onDelete, onBack }) => (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #f1f5f9", backgroundColor: "#fff" }}>
            <IconButton size="small" onClick={onBack} sx={{ color: PRIMARY, '&:hover': { background: PRIMARY_LIGHT } }}>
                <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Typography variant="subtitle2" fontWeight={700} color={PRIMARY}>
                Historique des conversations
            </Typography>
        </Box>

        {sessions.length === 0 ? (
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, color: "text.disabled" }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 40, opacity: 0.25 }} />
                <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '1rem' }}>Aucune conversation sauvegardée</Typography>
            </Box>
        ) : (
            <List disablePadding sx={{ flex: 1, overflowY: "auto", "&::-webkit-scrollbar": { width: 4 }, "&::-webkit-scrollbar-thumb": { backgroundColor: "#aed6f1", borderRadius: 2 } }}>
                {[...sessions].reverse().map((s) => (
                    <ListItemButton
                        key={s.id}
                        selected={s.id === currentId}
                        onClick={() => onSelect(s.id)}
                        sx={{
                            borderBottom: "1px solid #f8fafc",
                            "&.Mui-selected": { backgroundColor: PRIMARY_LIGHT },
                            "&.Mui-selected:hover": { backgroundColor: "#d6eaf8" },
                            pr: 5,
                        }}
                    >
                        <ListItemText
                            primary={
                                <Typography variant="body2" fontWeight={s.id === currentId ? 600 : 400} noWrap sx={{ fontSize: "1rem" }}>
                                    {sessionTitle(s)}
                                </Typography>
                            }
                            secondary={
                                <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.7rem" }}>
                                    {formatDate(s.createdAt)} · {s.messages.filter((m) => m.role === "user").length} message{s.messages.filter((m) => m.role === "user").length > 1 ? "s" : ""}
                                </Typography>
                            }
                        />
                        <ListItemSecondaryAction>
                            <IconButton
                                edge="end"
                                size="small"
                                onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
                                sx={{ color: "#ccc", "&:hover": { color: "#e53935" } }}
                            >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItemButton>
                ))}
            </List>
        )}
    </Box>
);


const parseChatResponse = (data) => {
    if (data?.type === "candidates") {
        const candidates = (data?.Candidates || [])
            .filter((c) => c.score == null || c.score > 0.01)
            .map((c) => ({ ...(c.properties || c), score: c.score, uploadId: c.id, candidatId: c.id }));
        const count = candidates.length;
        return {
            text: data.message || (count > 0 ? `J'ai trouvé ${count} profil${count > 1 ? "s" : ""} correspondant à votre recherche :` : "Aucun candidat trouvé pour ces critères."),
            candidates,
        };
    }
    return { text: data?.message || data || "Je suis là pour vous aider.", candidates: [] };
};


export default function ChatbotIA() {
    const userId = getUserId();

    const [sessions, setSessions] = useState(() => {
        const saved = loadHistory(userId);
        if (saved?.sessions?.length) return saved.sessions;
        return [makeSession()];
    });

    const [currentSessionId, setCurrentSessionId] = useState(() => {
        const saved = loadHistory(userId);
        return saved?.currentSessionId || sessions[0]?.id;
    });

    const [open, setOpen] = useState(false);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [fabPos, setFabPos] = useState(loadFabPos);
    const [dragging, setDragging] = useState(false);
    const dragRef = useRef(null);
    const suppressClickRef = useRef(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const sendMessageRef = useRef(null);
    const routerHistory = useHistory();

    const currentSession = sessions.find((s) => s.id === currentSessionId) || sessions[0];
    const messages = currentSession?.messages || [];

    useEffect(() => {
        saveHistory(userId, { sessions, currentSessionId });
    }, [sessions, currentSessionId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, loading]);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 150);
    }, [open]);

    const [autosend, setAutosend] = useState(() => {
        const p = sessionStorage.getItem("pia_prefill");
        if (p) sessionStorage.removeItem("pia_prefill");
        return p || "";
    });

    const updateCurrentMessages = useCallback((updater) => {
        setSessions((prev) => prev.map((s) =>
            s.id === currentSessionId
                ? { ...s, messages: typeof updater === "function" ? updater(s.messages) : updater }
                : s
        ));
    }, [currentSessionId]);

    const handleNewChat = () => {
        const s = makeSession();
        setSessions((prev) => [...prev, s]);
        setCurrentSessionId(s.id);
        setInput("");
        setShowHistory(false);
    };

    const handleClearChat = () => {
        updateCurrentMessages([{ role: "assistant", content: GREETING, candidates: [] }]);
        setInput("");
    };

    const handleSelectSession = (id) => {
        setCurrentSessionId(id);
        setShowHistory(false);
    };

    const handleDeleteSession = (id) => {
        setSessions((prev) => {
            const next = prev.filter((s) => s.id !== id);
            if (next.length === 0) {
                const fresh = makeSession();
                setCurrentSessionId(fresh.id);
                return [fresh];
            }
            if (id === currentSessionId) {
                setCurrentSessionId(next[next.length - 1].id);
            }
            return next;
        });
    };


    const sendMessage = useCallback(async (textOverride) => {
        const text = (textOverride ?? input).trim();
        if (!text || loading) return;

        setInput("");
        updateCurrentMessages((prev) => [...prev, { role: "user", content: text, candidates: [] }]);
        setLoading(true);

        try {
            const threadId = `${userId}_${currentSessionId}`;
            const res = await chatMessage(text, threadId);
            const { text: replyText, candidates } = parseChatResponse(res?.data);
            updateCurrentMessages((prev) => [...prev, { role: "assistant", content: replyText, candidates }]);
        } catch {
            updateCurrentMessages((prev) => [...prev, { role: "assistant", content: "Une erreur est survenue. Veuillez réessayer.", candidates: [] }]);
        } finally {
            setLoading(false);
            setTimeout(() => inputRef.current?.focus(), 60);
        }
    }, [input, loading, currentSessionId, userId, updateCurrentMessages]);
    sendMessageRef.current = sendMessage;

    useEffect(() => {
        if (!autosend) return;
        setOpen(true);
        const t = setTimeout(() => {
            sendMessageRef.current?.(autosend);
            setAutosend("");
        }, 400);
        return () => clearTimeout(t);
    }, [autosend]);

    useEffect(() => {
        const handler = (e) => {
            const msg = e.detail?.message;
            if (msg) setAutosend(msg);
        };
        window.addEventListener("pia:autosend", handler);
        return () => window.removeEventListener("pia:autosend", handler);
    }, []);
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    };

    useEffect(() => {
        const onResize = () => setFabPos((p) => clampFabPos(p));
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const handleFabPointerDown = (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        dragRef.current = { startX: e.clientX, startY: e.clientY, startPos: fabPos, moved: false };
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const handleFabPointerMove = (e) => {
        const d = dragRef.current;
        if (!d) return;
        const dx = e.clientX - d.startX;
        const dy = e.clientY - d.startY;
        // Small threshold so a simple click still opens the chat
        if (!d.moved && Math.abs(dx) + Math.abs(dy) < 5) return;
        if (!d.moved) { d.moved = true; setDragging(true); }
        setFabPos(clampFabPos({ right: d.startPos.right - dx, bottom: d.startPos.bottom - dy }));
    };

    const handleFabPointerUp = (e) => {
        const d = dragRef.current;
        dragRef.current = null;
        if (e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
        if (!d?.moved) return;
        suppressClickRef.current = true;
        setDragging(false);
        setFabPos((p) => {
            try { localStorage.setItem(FAB_POS_KEY, JSON.stringify(p)); } catch {}
            return p;
        });
    };

    const handleFabClick = () => {
        if (suppressClickRef.current) { suppressClickRef.current = false; return; }
        setOpen((v) => !v);
    };

    // The panel opens on the side of the screen where there is room next to the button
    const fabOnLeft = fabPos.right + FAB_SIZE / 2 > window.innerWidth / 2;
    const fabOnTop = fabPos.bottom + FAB_SIZE / 2 > window.innerHeight / 2;
    const panelGap = FAB_SIZE + 10;
    const panelVertical = fabOnTop
        ? { top: window.innerHeight - fabPos.bottom - FAB_SIZE + panelGap }
        : { bottom: fabPos.bottom + panelGap };
    const panelSpace = fabOnTop
        ? window.innerHeight - (window.innerHeight - fabPos.bottom - FAB_SIZE + panelGap) - 12
        : window.innerHeight - (fabPos.bottom + panelGap) - 12;
    const panelHorizontal = fabOnLeft
        ? { left: { xs: 12, sm: Math.max(12, window.innerWidth - fabPos.right - FAB_SIZE) }, right: { xs: 12, sm: "auto" } }
        : { right: { xs: 12, sm: Math.max(12, fabPos.right) } };

    const handleCandidateClick = (candidate) => {
        const candidatId = candidate.candidatId || candidate.uploadId || candidate.id;
        if (candidatId) {
            routerHistory.push(`/candidats/${candidatId}/view`);
        } else {
            const params = new URLSearchParams({ categorie: "candidat", isAiSearch: "true", iaMode: "agent", agentText: candidate.full_name || "" });
            routerHistory.push(`/accueil/chercher?${params.toString()}`);
        }
        setOpen(false);
    };

    const showSuggestions = messages.length === 1 && !loading && !showHistory;
    const historyCount = sessions.filter((s) => s.messages.some((m) => m.role === "user")).length;

    return (
        <>
            <Slide direction={fabOnTop ? "down" : "up"} in={open} mountOnEnter unmountOnExit>
                <Paper elevation={12} sx={{
                    position: "fixed", ...panelVertical, ...panelHorizontal, zIndex: 1300,
                    width: { xs: "calc(100vw - 24px)", sm: 460 }, height: 660,
                    maxWidth: 460, maxHeight: Math.max(320, panelSpace),
                    display: "flex", flexDirection: "column",
                    borderRadius: 4, overflow: "hidden",
                    border: "1px solid #d6eaf8",
                    boxShadow: "0 20px 60px rgba(99,102,241,0.18), 0 4px 16px rgba(0,0,0,0.08)",
                }}>
                    <Box sx={{
                        background: "linear-gradient(135deg, #03407b 0%, #02357a 50%, #022d68 100%)",
                        px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.25,
                    }}>
                        <Avatar sx={{
                            width: 40, height: 40,
                            background: "rgba(255,255,255,0.18)",
                            border: "1.5px solid rgba(255,255,255,0.3)",
                            backdropFilter: "blur(4px)",
                        }}>
                            <AutoAwesomeIcon sx={{ fontSize: 20, color: "#fff" }} />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" color="#fff" fontWeight={800} lineHeight={1.2} sx={{ fontSize: "1rem", letterSpacing: "0.01em" }}>Gia</Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "#34d399", boxShadow: "0 0 6px #34d399" }} />
                                <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "1rem" }}>Assistante RH · Go&Dev</Typography>
                            </Box>
                        </Box>

                        <Tooltip title="Historique" placement="bottom">
                            <IconButton size="small" onClick={() => setShowHistory((v) => !v)} sx={{ color: showHistory ? "#fff" : "rgba(255,255,255,0.75)", backgroundColor: showHistory ? "rgba(255,255,255,0.18)" : "transparent", "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.12)" } }}>
                                <HistoryIcon sx={{ fontSize: 17 }} />
                                {historyCount > 0 && !showHistory && (
                                    <Box sx={{ position: "absolute", top: 2, right: 2, width: 7, height: 7, borderRadius: "50%", backgroundColor: "#fff", opacity: 0.9 }} />
                                )}
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Effacer la conversation" placement="bottom">
                            <IconButton size="small" onClick={handleClearChat} disabled={loading} sx={{ color: "rgba(255,255,255,0.75)", "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.12)" } }}>
                                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Nouvelle conversation" placement="bottom">
                            <IconButton size="small" onClick={handleNewChat} disabled={loading} sx={{ color: "rgba(255,255,255,0.75)", "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.12)" } }}>
                                <AddCommentIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>

                        <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: "rgba(255,255,255,0.85)", "&:hover": { color: "#fff" } }}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>

                    <Divider />

                    {showHistory ? (
                        <HistoryPanel
                            sessions={sessions}
                            currentId={currentSessionId}
                            onSelect={handleSelectSession}
                            onDelete={handleDeleteSession}
                            onBack={() => setShowHistory(false)}
                        />
                    ) : (
                        <>
                            <Box sx={{
                                flex: 1, overflowY: "auto", p: 2, backgroundColor: "#f8fafc",
                                backgroundImage: "radial-gradient(circle at 20% 20%, rgba(99,102,241,0.03) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(79,70,229,0.03) 0%, transparent 50%)",
                                "&::-webkit-scrollbar": { width: 4 },
                                "&::-webkit-scrollbar-thumb": { backgroundColor: "#aed6f1", borderRadius: 2 },
                            }}>
                                {messages.map((msg, i) => (
                                    <MessageBubble key={i} message={msg} onCandidateClick={handleCandidateClick} />
                                ))}

                                {loading && (
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
                                        <Avatar sx={{ width: 28, height: 28, background: "linear-gradient(135deg, #0556a4, #03407b)", mt: 0.25, boxShadow: "0 2px 6px rgba(99,102,241,0.3)" }}>
                                            <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                                        </Avatar>
                                        <Paper elevation={0} sx={{ px: 1.5, py: 0.75, borderRadius: 2.5, borderTopLeftRadius: 0, background: "#fff", border: "1px solid #f1f5f9" }}>
                                            <TypingDots />
                                        </Paper>
                                    </Box>
                                )}

                                {showSuggestions && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, letterSpacing: "0.07em", color: "#94a3b8", textTransform: "uppercase", mb: 1 }}>
                                            Suggestions rapides
                                        </Typography>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                            {SUGGESTIONS.map((s, i) => (
                                                <Box key={i} onClick={() => sendMessage(s)} sx={{
                                                    px: 1.5, py: 0.9,
                                                    borderRadius: 2.5,
                                                    border: "1.5px solid #aed6f1",
                                                    background: "#fff",
                                                    color: PRIMARY,
                                                    fontSize: "1rem",
                                                    fontWeight: 500,
                                                    cursor: "pointer",
                                                    transition: "all 0.15s ease",
                                                    "&:hover": {
                                                        background: PRIMARY_LIGHT,
                                                        borderColor: PRIMARY,
                                                        transform: "translateX(3px)",
                                                    },
                                                }}>
                                                    {s}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                <div ref={messagesEndRef} />
                            </Box>

                            <Box sx={{ px: 1.75, py: 1.25, backgroundColor: "#fff", borderTop: "1px solid #f1f5f9", display: "flex", gap: 1, alignItems: "flex-end" }}>
                                <TextField
                                    inputRef={inputRef}
                                    fullWidth size="small" multiline maxRows={3}
                                    placeholder="Décrivez le profil recherché..."
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    disabled={loading}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 3,
                                            fontSize: "1rem",
                                            background: "#f8fafc",
                                            "& fieldset": { borderColor: "#e2e8f0" },
                                            "&:hover fieldset": { borderColor: "#7fb3d3" },
                                            "&.Mui-focused fieldset": { borderColor: PRIMARY, borderWidth: 1.5 },
                                        }
                                    }}
                                />
                                <IconButton onClick={() => sendMessage()} disabled={!input.trim() || loading}
                                    sx={{
                                        width: 40, height: 40, borderRadius: 2.5, flexShrink: 0,
                                        background: !input.trim() || loading ? "#e2e8f0" : "linear-gradient(135deg, #0556a4, #03407b)",
                                        color: "#fff",
                                        boxShadow: !input.trim() || loading ? "none" : "0 2px 8px rgba(99,102,241,0.4)",
                                        transition: "all 0.18s ease",
                                        "&:hover": { background: "linear-gradient(135deg, #03407b, #022d68)", transform: "scale(1.05)" },
                                        "&.Mui-disabled": { backgroundColor: "#e2e8f0", color: "#94a3b8" },
                                    }}>
                                    {loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SendIcon sx={{ fontSize: 18 }} />}
                                </IconButton>
                            </Box>
                        </>
                    )}
                </Paper>
            </Slide>

            <Tooltip title={open || dragging ? "" : "Gia — glisser pour déplacer"} placement={fabOnLeft ? "right" : "left"}>
                <Fab onClick={handleFabClick} aria-label="Ouvrir Gia"
                    onPointerDown={handleFabPointerDown}
                    onPointerMove={handleFabPointerMove}
                    onPointerUp={handleFabPointerUp}
                    onPointerCancel={handleFabPointerUp}
                    sx={{
                        position: "fixed", bottom: fabPos.bottom, right: fabPos.right, zIndex: 1300,
                        width: FAB_SIZE, height: FAB_SIZE,
                        touchAction: "none",
                        cursor: dragging ? "grabbing" : "grab",
                        background: open
                            ? "linear-gradient(135deg, #03407b, #022d68)"
                            : "linear-gradient(135deg, #03407b 0%, #02357a 100%)",
                        color: "#fff",
                        boxShadow: open
                            ? "0 4px 16px rgba(99,102,241,0.5)"
                            : "0 6px 24px rgba(99,102,241,0.45)",
                        transition: dragging ? "none" : "background 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease",
                        border: "2px solid rgba(255,255,255,0.15)",
                        "&:hover": {
                            background: "linear-gradient(135deg, #03407b, #022d68)",
                            transform: "scale(1.07) translateY(-2px)",
                            boxShadow: "0 8px 28px rgba(99,102,241,0.55)",
                        },
                    }}>
                    {open ? <CloseIcon sx={{ fontSize: 26 }} /> : <SmartToyIcon sx={{ fontSize: 26 }} />}
                </Fab>
            </Tooltip>
        </>
    );
}
